/**
 * Calendar half of the planner capability.
 *
 * Supplies exactly two seams: `setCalendarDate` and `scheduleWpPublish`. The
 * mount merges this with the feed and table halves into `installCapabilities`,
 * so what this file omits is unreachable from a calendar command by
 * construction — there is no publish here, no create, no delete.
 *
 * Both bodies call the SAME service layer the forms call. The spec warns that
 * UI and tool paths "may have different validation logic" (`index.bs:1598`),
 * and a parallel path is how a tool quietly skips a check the form performs.
 * Never a page handler: page code can open `window.confirm`, which a tool path
 * cannot answer and the browser blocks.
 */
import type { CommitAuthorization, ReadDeps, Receipt, Refusal, WriteDeps } from './contract';
import { blogCreationService } from '../services/blogCreationService';
import { contentPlannerService } from '../services/contentPlannerService';
import { useTenantStore } from '../stores/useTenantStore';

function refuse(because: string, retryable = false): Refusal {
  return { kind: 'refused', because, retryable };
}

/**
 * The tenant the runtime minted must still be the tenant the tab is on.
 *
 * WHY THIS EXISTS, because it looks redundant and is not. Neither service
 * function below accepts a tenant argument: `blogCreationService.schedule`
 * takes `(contentId, scheduledTime)` and resolves the tenant itself through
 * `getTenantId()`, which reads `useTenantStore.currentTenant`. The runtime
 * mints its `TenantId` from `useWorkspaceStore.selection` instead.
 *
 * Those are not two sources of truth: `useTenantStore` is a documented
 * READ-ONLY FACADE derived one-way from `useWorkspaceStore`, with no setters
 * (`useTenantStore.ts:2-19`). So they cannot disagree about what is selected.
 *
 * They CAN disagree about WHEN. The runtime resolves the tenant in `resolve`,
 * the operator can switch workspace while the command is still running, and
 * the service then reads the new selection at `run` time. The write would land
 * on a different client than the one the diff was approved for. This closes
 * that window by re-reading at the moment of the call and refusing on drift,
 * which is the only way to honour the contract's "tenant explicit, always"
 * through a service that does not take one.
 */
function tenantDrift(auth: CommitAuthorization): Refusal | null {
  const current = useTenantStore.getState().currentTenant;
  if (!current) {
    return refuse('No workspace is selected in this tab, so there is no tenant to write to.');
  }
  if (current !== auth.tenant) {
    return refuse(
      `The selected workspace changed while this command was running (approved for ${auth.tenant}, now ${current}). Nothing was written. Re-run it if you still want the change.`,
      true,
    );
  }
  return null;
}

function isExpired(auth: CommitAuthorization): boolean {
  return Number.isFinite(auth.expiresAt) && Date.now() > auth.expiresAt;
}

export const calendarSeams: Partial<ReadDeps & WriteDeps> = {
  /**
   * Calendar display date ONLY. `PATCH /content-planner/schedule/{id}` via
   * `contentPlannerService.scheduleContent`. Nothing reaches WordPress, which
   * is the entire reason this is not the same seam as `scheduleWpPublish`.
   */
  async setCalendarDate(
    auth: CommitAuthorization,
    articleId: string,
    date: string,
  ): Promise<Receipt | Refusal> {
    if (isExpired(auth)) return refuse('The authorization for this change expired before it was applied.', true);
    const drift = tenantDrift(auth);
    if (drift) return drift;

    const result = await contentPlannerService.scheduleContent(articleId, date);

    // The service resolves `success: false` rather than throwing on a declined
    // write, and a falsy success reported as a Receipt is a write that never
    // happened being announced as one.
    if (!result?.success) {
      return refuse('The planner declined the date change and the article was not moved.', true);
    }

    return {
      summary: `Moved to ${result.scheduled_date ?? date} on the calendar. This is the planner date only; nothing was sent to WordPress.`,
      facts: {
        contentId: result.content_id ?? articleId,
        calendarDate: result.scheduled_date ?? date,
        reachedWordPress: false,
      },
    };
  },

  /**
   * WordPress publish schedule. `blogCreationService.schedule`, which syncs a
   * WP draft and makes it go live at the given time. `writes-live-site`.
   *
   * Takes a `Date` and serialises here rather than accepting a string, so a
   * caller cannot hand the WordPress system a bare `YYYY-MM-DD` that the
   * calendar seam would have accepted. The two seams speak different types on
   * purpose.
   */
  async scheduleWpPublish(
    auth: CommitAuthorization,
    articleId: string,
    publishAt: Date,
  ): Promise<Receipt | Refusal> {
    if (isExpired(auth)) return refuse('The authorization for this change expired before it was applied.', true);
    const drift = tenantDrift(auth);
    if (drift) return drift;

    if (Number.isNaN(publishAt.getTime())) {
      return refuse('publishAt is not a valid instant.');
    }
    if (publishAt.getTime() <= Date.now()) {
      return refuse(
        'publishAt is in the past. Scheduling a WordPress publish for a past time makes it go live immediately, which is not what "schedule" should do.',
      );
    }

    const result = await blogCreationService.schedule(articleId, publishAt.toISOString());

    if (!result?.id) {
      return refuse('WordPress scheduling did not confirm, so the publish time is unverified.', true);
    }

    // DO NOT CLAIM WORDPRESS WAS REACHED FROM A ROW ID.
    //
    // This used to report `reachedWordPress: true` whenever `result.id` came
    // back. A row id only proves the request returned. The backend skips
    // WordPress entirely when `publish_immediately` is false, so the row can
    // carry a scheduled time while nothing was ever sent, and the receipt
    // would have told an agent a client's live site had been touched when it
    // had not. What is actually known is the row's publish_status, so that is
    // what gets reported, and the summary says scheduled rather than sent.
    const publishStatus = result.publish_status ?? null;

    return {
      summary:
        `Scheduled for ${result.scheduled_publish_time ?? publishAt.toISOString()}` +
        (publishStatus ? ` (publish status: ${publishStatus}).` : '.') +
        ' The planner holds the schedule; whether WordPress has been contacted yet is not confirmed by this call.',
      facts: {
        contentId: result.id,
        scheduledPublishTime: result.scheduled_publish_time ?? publishAt.toISOString(),
        publishStatus: publishStatus ?? 'unreported',
      },
    };
  },
};

/** Default export is what the mount merges by name into installCapabilities. */
export default calendarSeams;
